import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";

interface FormulaConfig {
    expression: string;
    outputFormat?: "WholeNumber" | "Decimal";
}

interface SubcategoryFormulas {
    [subcategory: string]: FormulaConfig;
}

interface ProductFormula {
    expression?: string;
    outputFormat?: "WholeNumber" | "Decimal";
    subcategories?: SubcategoryFormulas;
}

export interface Formulas {
    [product: string]: ProductFormula;
}

export default function useFormulas() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();
    const queryClient = useQueryClient();

    function useList() {
        return useQuery<Formulas>({
            queryKey: ["formulas"],
            queryFn: async () => {
                const response = await axiosPrivate.get("/v1/formulas/list");
                return parseSuccessResponseData(response) as Formulas;
            },
        });
    }

    function useSave() {
        return useMutation({
            mutationFn: async (data: Formulas) => {
                const response = await axiosPrivate.put("/v1/formulas/save", data);
                return parseSuccessResponseData(response);
            },
            onSuccess: () => {
                // Invalidate the cache so the next read picks up the saved version
                queryClient.invalidateQueries({ queryKey: ["formulas"] });
            },
        });
    }

    return { useList, useSave };
}
